import { Logger } from '../support/Logger';
import { ILogger } from '../support/ILogger';

export type IAvatarImageStatus = 'loading' | 'loaded' | 'failed';

/**
 * Loading is injected rather than called directly so the state machine can be exercised
 * without a DOM: `new Image()` does not exist under the unit test runner.
 */
export interface IImageLoader {
    load: (url: string) => Promise<void>;
}

/**
 * Loads off-document, so a URL that fails never reaches the DOM and the browser has nothing
 * to draw a broken-image indicator into.
 */
export class BrowserImageLoader implements IImageLoader {
    load = (url: string): Promise<void> => {
        return new Promise((resolve, reject) => {
            const image = new Image();
            // Google's photo host intermittently refuses http://localhost, a referrer our Android
            // app shares with many others. Google's photos are cacheable, so the visible element
            // paints from what this fetched instead of asking again.
            image.referrerPolicy = 'no-referrer';
            image.onload = () => resolve();
            image.onerror = () => reject(new Error(`could not load ${url}`));
            image.src = url;
        });
    };
}

export interface IAvatarImageRequest {
    url: string;
    onStatus: (status: IAvatarImageStatus) => void;
    timeoutMs?: number;
    retryDelaysMs?: number[];
}

/**
 * What became of one attempt. A url that fails intermittently looks the same in a report either
 * way, so the report carries which it was: 'error' means the request was refused or the image was
 * unusable, 'timeout' means it had not finished within the budget, which a slow download reaches
 * as readily as a request nothing ever answers.
 */
export interface IAvatarLoadAttempt {
    outcome: 'loaded' | 'timeout' | 'error';
    elapsedMs: number;
}

// Short enough that a hung request surfaces as a visible failure quickly. Retries continue after
// that, so a slow network costs a moment of placeholder rather than a long blank circle.
const DEFAULT_TIMEOUT_MS = 3000;
const DEFAULT_RETRY_DELAYS_MS = [1000, 3000];

export class AvatarImageLoader {
    static inject = () => [BrowserImageLoader, Logger];
    constructor(private imageLoader: IImageLoader, private logger: ILogger) {}

    // An image that fails is usually drawn wherever its person appears, so each is reported once.
    private reportedFailures = new Set<string>();

    /**
     * Reports 'loading' immediately, then 'failed' as soon as the first attempt gives up, and
     * 'loaded' if any attempt succeeds. Failing fast and retrying underneath means a transient
     * cold-start failure still self-corrects without the caller holding a blank-looking circle
     * for the whole retry budget.
     *
     * Returns a cancel function that must be called on unmount and whenever the url changes, or
     * a late result from an abandoned request will overwrite newer state.
     */
    load = ({
        url,
        onStatus,
        timeoutMs = DEFAULT_TIMEOUT_MS,
        retryDelaysMs = DEFAULT_RETRY_DELAYS_MS
    }: IAvatarImageRequest): (() => void) => {
        let cancelled = false;

        const emit = (status: IAvatarImageStatus) => {
            if (!cancelled) {
                onStatus(status);
            }
        };

        emit('loading');

        const attempts: IAvatarLoadAttempt[] = [];

        const attempt = async (remainingDelays: number[]): Promise<void> => {
            const result = await this.tryOnce(url, timeoutMs);
            attempts.push(result);
            if (cancelled) {
                return;
            }
            if (result.outcome === 'loaded') {
                emit('loaded');
                return;
            }

            emit('failed');

            if (remainingDelays.length === 0) {
                this.reportFailure(url, attempts);
                return;
            }

            const [delay, ...rest] = remainingDelays;
            await this.wait(delay);
            if (cancelled) {
                return;
            }
            return attempt(rest);
        };

        // Deliberately unguarded. tryOnce turns a rejected load into an attempt record, so attempt
        // only rejects if onStatus itself throws -- a defect in the caller, which should surface
        // rather than be reported back through the same throwing callback.
        attempt(retryDelaysMs);

        return () => {
            cancelled = true;
        };
    };

    /**
     * A request that never answers is a failure, not perpetual loading. Without the timeout the
     * caller would sit on the neutral placeholder indefinitely.
     */
    private tryOnce = async (url: string, timeoutMs: number): Promise<IAvatarLoadAttempt> => {
        // Monotonic, so a clock correction mid-load cannot report a duration that never happened.
        const startedAt = performance.now();
        let timer: ReturnType<typeof setTimeout> | undefined;

        const timedOut = new Promise<IAvatarLoadAttempt['outcome']>(resolve => {
            timer = setTimeout(() => resolve('timeout'), timeoutMs);
        });
        const loaded = (async (): Promise<IAvatarLoadAttempt['outcome']> => {
            try {
                await this.imageLoader.load(url);
                return 'loaded';
            }
            catch {
                // Dropped deliberately: the message embeds the url with its media token, and which
                // of the two cases this was is the only part worth reporting.
                return 'error';
            }
        })();

        const outcome = await Promise.race([loaded, timedOut]);
        if (timer) {
            clearTimeout(timer);
        }
        return { outcome, elapsedMs: Math.round(performance.now() - startedAt) };
    };

    // Logger sends nothing while the device is offline, when every image would fail. Without the
    // query string, which can carry a media token.
    private reportFailure = (url: string, attempts: IAvatarLoadAttempt[]) => {
        // a local preview carries the picture itself
        if (/^(data|blob):/i.test(url)) {
            return;
        }
        const location = url.split('?')[0];
        if (this.reportedFailures.has(location)) {
            return;
        }
        this.reportedFailures.add(location);
        this.logger.error(new Error('avatar image could not be loaded'), { url: location, attempts });
    };

    private wait = (ms: number): Promise<void> => {
        return new Promise(resolve => setTimeout(resolve, ms));
    };
}
